<template>
  <div class="min-h-screen bg-gray-50 py-12">
    <div class="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
      <!-- Breadcrumb -->
      <nav class="mb-8">
        <ol class="flex flex-wrap items-center space-x-2 text-sm text-gray-500">
          <li><router-link to="/" class="hover:text-blue-600">Trang chủ</router-link></li>
          <li>/</li>
          <li><router-link to="/blog" class="hover:text-blue-600">Blog</router-link></li>
          <li>/</li>
          <li class="text-gray-900">Chặn rò rỉ IP/DNS/WebRTC</li>
        </ol>
      </nav>

      <!-- Article Header -->
      <header class="mb-8">
        <h1 class="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-4">Bảo vệ danh tính trực tuyến: Chặn rò rỉ IP/DNS/WebRTC 2025</h1>
        <div class="flex items-center text-sm text-gray-500 mb-6">
          <span>—</span>
          <span class="mx-2">•</span>
          <span>6 phút đọc</span>
          <span class="mx-2">•</span>
          <span>Quyền riêng tư</span>
        </div>
        <div class="h-48 sm:h-56 lg:h-64 bg-gradient-to-br from-sky-500 to-blue-700 rounded-lg flex items-center justify-center">
          <svg class="w-16 h-16 sm:w-20 sm:h-20 lg:w-24 lg:h-24 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
          </svg>
        </div>
      </header>

      <!-- Article Content -->
      <article class="bg-white rounded-lg shadow-lg p-4 sm:p-6 lg:p-8">
        <div class="prose prose-lg max-w-none">
          <h2>Rò rỉ IP/DNS/WebRTC là gì và tại sao nguy hiểm?</h2>
          <p>Ngay cả khi sử dụng VPN, danh tính thực của bạn vẫn có thể bị lộ qua các kênh khác nhau. Đây là vấn đề bảo mật nghiêm trọng có thể:</p>
          
          <div class="bg-red-50 p-4 sm:p-6 rounded-lg my-6">
            <h3 class="text-xl font-semibold mb-4 text-red-800">⚠️ Hậu quả của rò rỉ thông tin</h3>
            <ul class="space-y-2 text-red-700">
              <li>• <strong>Lộ địa chỉ IP thật:</strong> ISP và chính phủ có thể theo dõi</li>
              <li>• <strong>DNS leak:</strong> Truy vấn DNS không qua VPN</li>
              <li>• <strong>WebRTC leak:</strong> Browser tiết lộ IP thật qua WebRTC</li>
              <li>• <strong>Time zone leak:</strong> Lộ múi giờ và vị trí</li>
              <li>• <strong>Font fingerprinting:</strong> Nhận dạng thiết bị qua font</li>
            </ul>
          </div>

          <h2>1. Kiểm tra rò rỉ IP/DNS</h2>
          <div class="bg-blue-50 p-4 sm:p-6 rounded-lg my-6">
            <h3 class="font-semibold mb-4 text-blue-800">🔍 Công cụ kiểm tra rò rỉ</h3>
            <div class="grid md:grid-cols-2 gap-4">
              <div class="bg-white p-4 rounded-lg">
                <h4 class="font-semibold text-blue-600">IP Leak Test</h4>
                <ul class="text-sm space-y-1">
                  <li>• <a href="https://whatismyip.com" class="text-blue-600 underline">whatismyip.com</a></li>
                  <li>• <a href="https://ipleak.net" class="text-blue-600 underline">ipleak.net</a></li>
                  <li>• <a href="https://dnsleaktest.com" class="text-blue-600 underline">dnsleaktest.com</a></li>
                </ul>
              </div>
              <div class="bg-white p-4 rounded-lg">
                <h4 class="font-semibold text-green-600">DNS Leak Test</h4>
                <ul class="text-sm space-y-1">
                  <li>• <a href="https://1.1.1.1/help" class="text-blue-600 underline">1.1.1.1/help</a></li>
                  <li>• <a href="https://dnsleaktest.com" class="text-blue-600 underline">dnsleaktest.com</a></li>
                  <li>• <a href="https://ipleak.net" class="text-blue-600 underline">ipleak.net</a></li>
                </ul>
              </div>
            </div>
          </div>

          <h3>Cách kiểm tra thủ công:</h3>
          <div class="bg-gray-900 text-green-400 p-3 sm:p-4 rounded-lg my-4 overflow-x-auto">
            <pre><code># Kiểm tra IP hiện tại
curl ifconfig.me
curl ipinfo.io

# Kiểm tra DNS
nslookup google.com
dig @1.1.1.1 google.com

# Kiểm tra WebRTC (JavaScript)
console.log("WebRTC IP:", window.RTCPeerConnection)</code></pre>
          </div>

          <h2>2. Khắc phục WebRTC Leak</h2>
          <p>WebRTC (Web Real-Time Communication) có thể tiết lộ IP thật ngay cả khi dùng VPN. Đây là vấn đề phổ biến nhất.</p>

          <h3>Firefox - Tắt WebRTC hoàn toàn:</h3>
          <div class="bg-gray-50 p-4 sm:p-6 rounded-lg my-6">
            <ol class="space-y-3">
              <li><strong>Mở about:config:</strong> Gõ <code>about:config</code> trong address bar</li>
              <li><strong>Chấp nhận rủi ro:</strong> Nhấn "Accept the Risk"</li>
              <li><strong>Tìm setting:</strong> Tìm <code>media.peerconnection.enabled</code></li>
              <li><strong>Đặt giá trị:</strong> Double-click để đổi thành <code>false</code></li>
              <li><strong>Restart browser:</strong> Khởi động lại Firefox</li>
            </ol>
          </div>

          <h3>Chrome - Sử dụng Extension:</h3>
          <div class="bg-gray-50 p-4 sm:p-6 rounded-lg my-6">
            <h4 class="font-semibold mb-3">Extensions được khuyến nghị:</h4>
            <ul class="space-y-2">
              <li><strong>WebRTC Leak Prevent:</strong> Tắt WebRTC hoàn toàn</li>
              <li><strong>uBlock Origin:</strong> Chặn WebRTC requests</li>
              <li><strong>Privacy Badger:</strong> Chặn tracking và WebRTC</li>
            </ul>
          </div>

          <h3>Chrome - Cấu hình thủ công:</h3>
          <div class="bg-gray-900 text-green-400 p-3 sm:p-4 rounded-lg my-4 overflow-x-auto">
            <pre><code># Thêm vào Chrome flags
chrome://flags/#disable-webrtc-hw-decoding
chrome://flags/#disable-webrtc-hw-encoding

# Hoặc sử dụng policy
{
  "WebRTCIPHandlingPolicy": "disable_non_proxied_udp"
}</code></pre>
          </div>

          <h2>3. Khắc phục DNS Leak</h2>
          <div class="bg-green-50 p-4 sm:p-6 rounded-lg my-6">
            <h3 class="font-semibold mb-4 text-green-800">🔧 Giải pháp DNS Leak</h3>
            <ol class="space-y-3">
              <li><strong>Bật DoH/DoT:</strong> Sử dụng DNS over HTTPS/TLS</li>
              <li><strong>VPN DNS:</strong> Đảm bảo VPN có DNS riêng</li>
              <li><strong>Custom DNS:</strong> Đặt DNS server thủ công</li>
              <li><strong>DNS leak protection:</strong> Bật trong VPN settings</li>
            </ol>
          </div>

          <h3>Cấu hình DNS an toàn:</h3>
          <div class="grid md:grid-cols-2 gap-4 my-4">
            <div class="border rounded-lg p-4">
              <h4 class="font-semibold text-blue-600">Cloudflare (Khuyến nghị)</h4>
              <code class="text-sm">1.1.1.1, 1.0.0.1</code>
              <p class="text-xs text-gray-600 mt-1">Nhanh, bảo mật, không log</p>
            </div>
            <div class="border rounded-lg p-4">
              <h4 class="font-semibold text-green-600">Quad9 (Bảo mật)</h4>
              <code class="text-sm">9.9.9.9, 149.112.112.112</code>
              <p class="text-xs text-gray-600 mt-1">Chặn malware, bảo mật cao</p>
            </div>
          </div>

          <h2>4. Khắc phục IPv6 Leak</h2>
          <p>IPv6 có thể bypass VPN và tiết lộ IP thật. Cần tắt IPv6 khi sử dụng VPN.</p>

          <h3>Windows - Tắt IPv6:</h3>
          <div class="bg-gray-50 p-4 sm:p-6 rounded-lg my-6">
            <ol class="space-y-3">
              <li><strong>Network Settings:</strong> Mở Network & Internet settings</li>
              <li><strong>Change adapter options:</strong> Chọn "Change adapter options"</li>
              <li><strong>Properties:</strong> Right-click adapter → Properties</li>
              <li><strong>Uncheck IPv6:</strong> Bỏ tick "Internet Protocol Version 6"</li>
              <li><strong>Apply:</strong> Nhấn OK và Apply</li>
            </ol>
          </div>

          <h3>macOS - Tắt IPv6:</h3>
          <div class="bg-gray-900 text-green-400 p-3 sm:p-4 rounded-lg my-4 overflow-x-auto">
            <pre><code># Tắt IPv6 qua Terminal
sudo networksetup -setv6off Wi-Fi
sudo networksetup -setv6off "USB Ethernet"

# Kiểm tra trạng thái
networksetup -getinfo Wi-Fi</code></pre>
          </div>

          <h2>5. Khắc phục Time Zone Leak</h2>
          <div class="bg-yellow-50 p-4 sm:p-6 rounded-lg my-6">
            <h3 class="font-semibold mb-4 text-yellow-800">⏰ Time Zone Protection</h3>
            <ul class="space-y-2 text-yellow-700">
              <li>• <strong>Browser Settings:</strong> Đặt timezone khác với vị trí thật</li>
              <li>• <strong>VPN Location:</strong> Chọn server cùng timezone</li>
              <li>• <strong>System Clock:</strong> Đồng bộ với VPN server</li>
              <li>• <strong>JavaScript:</strong> Sử dụng extension chặn timezone API</li>
            </ul>
          </div>

          <h2>6. Khắc phục Font Fingerprinting</h2>
          <p>Browser có thể bị nhận dạng qua danh sách font được cài đặt.</p>

          <h3>Giải pháp Font Fingerprinting:</h3>
          <ul class="space-y-2">
            <li><strong>Tor Browser:</strong> Sử dụng Tor Browser để ẩn font fingerprint</li>
            <li><strong>Canvas Blocker:</strong> Extension chặn canvas fingerprinting</li>
            <li><strong>Privacy Badger:</strong> Chặn tracking và fingerprinting</li>
            <li><strong>uBlock Origin:</strong> Chặn font requests</li>
          </ul>

          <h2>7. Kiểm tra toàn diện</h2>
          <div class="bg-blue-50 p-4 sm:p-6 rounded-lg my-6">
            <h3 class="font-semibold mb-4 text-blue-800">🔍 Comprehensive Leak Test</h3>
            <ol class="space-y-3 text-blue-700">
              <li><strong>IP Leak Test:</strong> Kiểm tra IP có khớp với VPN không</li>
              <li><strong>DNS Leak Test:</strong> Đảm bảo DNS queries qua VPN</li>
              <li><strong>WebRTC Test:</strong> Kiểm tra WebRTC không tiết lộ IP</li>
              <li><strong>IPv6 Test:</strong> Đảm bảo IPv6 bị chặn</li>
              <li><strong>Time Zone Test:</strong> Kiểm tra timezone khớp với VPN</li>
              <li><strong>Font Test:</strong> Kiểm tra font fingerprinting</li>
            </ol>
          </div>

          <h2>8. Best Practices</h2>
          <div class="bg-gray-50 p-4 sm:p-6 rounded-lg my-6">
            <h3 class="font-semibold mb-4">💡 Lời khuyên bảo mật</h3>
            <ul class="space-y-2 text-gray-700">
              <li>• <strong>Test định kỳ:</strong> Kiểm tra leak test mỗi tuần</li>
              <li>• <strong>VPN uy tín:</strong> Chọn VPN có leak protection</li>
              <li>• <strong>Browser hardening:</strong> Cấu hình browser bảo mật</li>
              <li>• <strong>Multiple layers:</strong> Kết hợp VPN + Tor + extensions</li>
              <li>• <strong>Regular updates:</strong> Cập nhật browser và extensions</li>
            </ul>
          </div>

          <div class="bg-red-50 p-6 rounded-lg mt-8">
            <h3 class="font-semibold mb-3 text-red-800">⚠️ Lưu ý quan trọng</h3>
            <p class="text-sm text-red-700">Không có giải pháp nào hoàn toàn ẩn danh 100%. Luôn kết hợp nhiều lớp bảo mật và test định kỳ để đảm bảo an toàn tối đa.</p>
          </div>
        </div>
      </article>

      <!-- Back Button -->
      <div class="mt-8 text-center">
        <router-link 
          to="/blog" 
          class="inline-flex items-center px-4 sm:px-6 py-2 sm:py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-colors duration-200 text-sm sm:text-base"
        >
          <svg class="mr-2 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"></path>
          </svg>
          Quay lại Blog
        </router-link>
      </div>
    </div>
  </div>
</template>

<script setup>
import { useHead } from '@vueuse/head'
const title = 'Bảo vệ danh tính trực tuyến: Chặn rò rỉ IP/DNS/WebRTC 2025'
const desc  = 'Hướng dẫn chi tiết kiểm tra và khắc phục rò rỉ IP/DNS/WebRTC. Bảo vệ danh tính trực tuyến và tăng cường bảo mật khi sử dụng VPN'
const url   = 'https://webservices.vn/blog/chan-ro-ri-ip-dns-webrtc'

useHead({
  title,
  meta: [
    { name: 'description', content: desc },
    { property: 'og:type', content: 'article' },
    { property: 'og:title', content: title },
    { property: 'og:description', content: desc },
    { property: 'og:url', content: url },
    { name: 'twitter:card', content: 'summary_large_image' },
    { name: 'twitter:title', content: title },
    { name: 'twitter:description', content: desc }
  ],
  link: [{ rel: 'canonical', href: url }]
})
</script>
